import {
  useState, useEffect, useCallback, useRef,
} from 'react';
import axios from 'axios';
import { message } from 'antd';
import csrfHeaders from '../../../../utils/csrfHeaders';

const usePendingApprovals = ({ enabled }) => {
  const [partners, setPartners] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const isMountedRef = useRef(true);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const fetchPendingApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/api/v1/external_partner_approvals', {
        params: { per_page: 50 },
      });
      if (!isMountedRef.current) return;
      setPartners(data.partners);
      setTotalCount(data.meta.total_count);
    } catch (err) {
      if (!isMountedRef.current) return;
      if (err.response?.status === 403) {
        setPartners([]);
        setTotalCount(0);
      } else {
        message.error('Failed to load pending approvals');
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      fetchPendingApprovals();
    }
  }, [enabled, fetchPendingApprovals]);

  const approvePartner = async (id) => {
    setActionLoading((prev) => ({ ...prev, [`${id}_approve`]: true }));
    try {
      await axios.post(`/api/v1/external_partner_approvals/${id}/approve`, {}, { headers: csrfHeaders() });
      if (!isMountedRef.current) return;
      setPartners((prev) => prev.filter((p) => p.id !== id));
      setTotalCount((prev) => Math.max(0, prev - 1));
      message.success('Partner approved');
    } catch {
      if (!isMountedRef.current) return;
      message.error('Failed to approve partner');
      fetchPendingApprovals();
    } finally {
      if (isMountedRef.current) setActionLoading((prev) => ({ ...prev, [`${id}_approve`]: false }));
    }
  };

  const rejectPartner = async (id) => {
    setActionLoading((prev) => ({ ...prev, [`${id}_reject`]: true }));
    try {
      await axios.post(`/api/v1/external_partner_approvals/${id}/reject`, {}, { headers: csrfHeaders() });
      if (!isMountedRef.current) return;
      setPartners((prev) => prev.filter((p) => p.id !== id));
      setTotalCount((prev) => Math.max(0, prev - 1));
      message.success('Partner rejected');
    } catch {
      if (!isMountedRef.current) return;
      message.error('Failed to reject partner');
      fetchPendingApprovals();
    } finally {
      if (isMountedRef.current) setActionLoading((prev) => ({ ...prev, [`${id}_reject`]: false }));
    }
  };

  return {
    partners,
    totalCount,
    loading,
    actionLoading,
    approvePartner,
    rejectPartner,
  };
};

export default usePendingApprovals;
